<template>
  <div id="app">

    <!-- Filters -->
    <div style="margin-bottom: 10px; display: flex; gap: 10px;">
      <dx-select-box
        :items="offices"
        display-expr="text"
        value-expr="id"
        placeholder="Select Office"
        :value="selectedOffice"
        @value-changed="onOfficeChanged"
        :show-clear-button="true"
        :width="250"
      />
      <dx-select-box
        :items="availableDoctors"
        display-expr="text"
        value-expr="id"
        placeholder="Select Doctor"
        :value="selectedDoctor"
        @value-changed="onDoctorChanged"
        :show-clear-button="true"
        :width="250"
      />
    </div>

    <!-- Doctor Color Legend -->
    <div style="margin-bottom: 10px; display: flex; gap: 12px; flex-wrap: wrap; align-items: center;">
      <span style="font-weight: 600; font-size: 13px;">Doctors:</span>
      <span
        v-for="doc in legendItems"
        :key="doc.id"
        style="display: inline-flex; align-items: center; gap: 4px; font-size: 13px;"
      >
        <span :style="{ display: 'inline-block', width: '14px', height: '14px', borderRadius: '3px', backgroundColor: doc.color }"></span>
        {{ doc.text }}
      </span>
      <span style="display: inline-flex; align-items: center; gap: 4px; font-size: 13px;">
        <span style="display: inline-block; width: 14px; height: 14px; border-radius: 3px; background-color: #d0d0d0;"></span>
        Booked
      </span>
    </div>

    <!-- Scheduler -->
    <dx-scheduler
      :key="schedulerKey"
      :data-source="displayItems"
      :current-date="currentDate"
      :start-day-hour="8"
      :end-day-hour="17"
      :views="views"
      :current-view="currentView"
      :cell-duration="15"
      :max-appointments-per-cell="'unlimited'"
      :editing="{ allowAdding: false, allowUpdating: false, allowDeleting: false }"
      @current-date-changed="onCurrentDateChanged"
      @current-view-changed="onCurrentViewChanged"
      @appointment-click="onSlotClick"
      appointment-template="appointmentTemplate"
    >
      <template #appointmentTemplate="{ data: { appointmentData } }">
        <div :style="getSlotStyle(appointmentData)" style="padding: 2px 4px; height: 100%; font-size: 11px;">
          <template v-if="appointmentData.slotType === 'lunch'">
            <strong>Lunch</strong>
          </template>
          <template v-else-if="appointmentData.slotType === 'booked'">
            <strong>{{ appointmentData.text }}</strong>
          </template>
          <template v-else>
            <span>&nbsp;</span>
          </template>
        </div>
      </template>
    </dx-scheduler>

    <!-- Booking Dialog -->
    <div v-if="showBookingDialog" class="booking-overlay">
      <div class="booking-dialog">
        <h3>{{ isEditing ? 'Edit Appointment' : 'Schedule Patient Visit' }}</h3>
        <div class="booking-field">
          <label>Date &amp; Time</label>
          <div>{{ formatDateTime(bookingSlot.startDate) }} &ndash; {{ formatTime(bookingEndTime) }}</div>
        </div>
        <div class="booking-field">
          <label>Duration</label>
          <select v-model="bookingDuration" class="booking-input">
            <option v-for="d in durationOptions" :key="d.value" :value="d.value">{{ d.text }}</option>
          </select>
        </div>
        <div class="booking-field">
          <label>Doctor</label>
          <dx-select-box
            :items="doctors"
            display-expr="text"
            value-expr="id"
            :value="bookingSlot.doctorId"
            @value-changed="(e) => bookingSlot.doctorId = e.value"
            :width="300"
            :drop-down-options="{ zIndex: 10001 }"
          />
        </div>
        <div class="booking-field">
          <label>Location</label>
          <dx-select-box
            :items="offices"
            display-expr="text"
            value-expr="id"
            :value="bookingSlot.locationId"
            @value-changed="(e) => bookingSlot.locationId = e.value"
            :width="300"
            :drop-down-options="{ zIndex: 10001 }"
          />
        </div>
        <div class="booking-field">
          <label>Patient Name</label>
          <input
            type="text"
            v-model="bookingSlot.patientName"
            placeholder="Enter patient name"
            class="booking-input"
          />
        </div>
        <div class="booking-field">
          <label>Appointment Type</label>
          <select v-model="bookingSlot.appointmentType" class="booking-input">
            <option value="" disabled selected>Select Appointment Type</option>
            <option v-for="t in appointmentTypes" :key="t.id" :value="t.id">{{ t.text }}</option>
          </select>
        </div>
        <div class="booking-field">
          <label>Visit Reason</label>
          <input
            type="text"
            v-model="bookingSlot.visitReason"
            placeholder="Enter visit reason"
            class="booking-input"
          />
        </div>
        <div class="booking-actions">
          <template v-if="isEditing">
            <button class="btn-book" @click="saveEdit" :disabled="!bookingSlot.patientName">Save</button>
            <button class="btn-danger" @click="cancelAppointment">Cancel Appointment</button>
            <button class="btn-cancel" @click="closeBookingDialog">Close</button>
          </template>
          <template v-else>
            <button class="btn-book" @click="confirmBooking" :disabled="!bookingSlot.patientName">Book</button>
            <button class="btn-cancel" @click="closeBookingDialog">Cancel</button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxScheduler from "devextreme-vue/scheduler";
import DxSelectBox from "devextreme-vue/select-box";

function generateDoctorSchedule() {
  const doctors = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const doctorNames = {
    1: "Lab and Radiology - Creve Coeur",
    2: "Lab and Radiology - Kennerly",
    3: "Lab and Radiology - Festus",
    4: "Lab and Radiology - Alton",
    5: "Chuck Willey MD",
    6: "Ehab Kaiser MD",
    7: "Raj Singh MD",
    8: "zzzzzzzEmil Fernando MD",
    9: "Becky Gatz APRN-BC",
    10: "Erin Holloway APRN-BC",
    11: "Wayne Scrimsher APRN-BC",
    12: "NH and Home Schedule RN"
  };
  const locations = [1, 2, 3, 4];
  const locationNames = { 1: "Creve Coeur", 2: "Kennerly", 3: "Festus", 4: "Alton" };

  // Assign each doctor/entity to a home office
  // Labs are fixed to their location; doctors assigned one each
  const doctorHomeOffice = {
    1: 1, 2: 2, 3: 3, 4: 4,     // Labs fixed to their location
    5: 1, 6: 2, 7: 3, 8: 4,     // MDs one per location
    9: 1, 10: 2, 11: 3, 12: 1   // APRNs/RN assigned
  };

  // IDs that are Lab entities (always full day, fixed to home office)
  const labIds = new Set([1, 2, 3, 4]);
  // IDs that are MDs or fixed-location entities (always at home office, random full/half day)
  const mdIds = new Set([5, 6, 7, 8, 12]);

  // Morning slots: 8:00-12:00 in 15-min increments (16 slots)
  // Lunch block: 12:00-13:00
  // Afternoon slots: 13:00-17:00 in 15-min increments (16 slots)
  const morningSlots = [];
  for (let h = 8; h < 12; h++) {
    for (let m = 0; m < 60; m += 15) {
      morningSlots.push({ hour: h, minute: m });
    }
  }
  const afternoonSlots = [];
  for (let h = 13; h < 17; h++) {
    for (let m = 0; m < 60; m += 15) {
      afternoonSlots.push({ hour: h, minute: m });
    }
  }
  const allDaySlots = [...morningSlots, ...afternoonSlots];

  const startDate = new Date(2026, 0, 1);   // Jan 1, 2026
  const endDate = new Date(2026, 6, 1);     // July 1, 2026

  const results = [];

  // Iterate day by day
  for (let d = new Date(startDate); d < endDate; d.setDate(d.getDate() + 1)) {
    const day = d.getDay();

    // Skip weekends
    if (day === 0 || day === 6) continue;

    const year = d.getFullYear();
    const month = d.getMonth();
    const date = d.getDate();

    doctors.forEach((doctorId) => {
      let locationId;
      let slotsToUse;

      if (labIds.has(doctorId)) {
        // Labs: always at their home office, full day every workday
        locationId = doctorHomeOffice[doctorId];
        slotsToUse = allDaySlots;
      } else if (mdIds.has(doctorId)) {
        // MDs: always at their home office, random full/half day
        locationId = doctorHomeOffice[doctorId];
        const isFullDay = Math.random() < 0.5;
        const isAM = Math.random() < 0.5;
        slotsToUse = isFullDay ? allDaySlots : (isAM ? morningSlots : afternoonSlots);
      } else {
        // APRNs/RN: 80% home office, 20% random, random full/half day
        const isHome = Math.random() < 0.8;
        locationId = isHome
          ? doctorHomeOffice[doctorId]
          : locations[Math.floor(Math.random() * locations.length)];
        const isFullDay = Math.random() < 0.5;
        const isAM = Math.random() < 0.5;
        slotsToUse = isFullDay ? allDaySlots : (isAM ? morningSlots : afternoonSlots);
      }

      slotsToUse.forEach((slot) => {
        results.push({
          text: "",
          doctorId,
          locationId,
          startDate: new Date(year, month, date, slot.hour, slot.minute),
          endDate: new Date(year, month, date, slot.hour, slot.minute + 15)
        });
      });

      // Add lunch block (not for Lab entities — they're available all day)
      if (!labIds.has(doctorId) && slotsToUse === allDaySlots) {
        results.push({
          text: "Lunch",
          doctorId,
          locationId,
          startDate: new Date(year, month, date, 12, 0),
          endDate: new Date(year, month, date, 13, 0),
          disabled: true
        });
      }
    });
  }

  return results;
}

const doctorSchedule = generateDoctorSchedule();

export default {
  components: {
    DxScheduler,
    DxSelectBox
  },

  data() {
    return {
      availabilitySlots: doctorSchedule,
      appointments: [],
      bookingVersion: 0,
      bookingDuration: 15,
      durationOptions: [],
      selectedOffice: 1,
      selectedDoctor: null,
      currentDate: new Date(),
      currentView: "Week",
      showBookingDialog: false,
      isEditing: false,
      editingIndex: -1,
      bookingSlot: {
        startDate: null,
        endDate: null,
        doctorId: null,
        locationId: null,
        patientName: "",
        appointmentType: null,
        visitReason: ""
      },

      views: [
        {
          type: "day",
          name: "Day"
        },
        {
          type: "week",
          name: "Week"
        }
      ],

      offices: [
        { id: 1, text: "Creve Coeur" },
        { id: 2, text: "Kennerly" },
        { id: 3, text: "Festus" },
        { id: 4, text: "Alton" }
      ],

      appointmentTypes: [
        { id: "BLOCK30", text: "Block 30 minutes" },
        { id: "COLLAB30", text: "Collaboration 30" },
        { id: "COLLAB45", text: "Collaboration 45" },
        { id: "ECHO60", text: "ECHO 60 minutes" },
        { id: "HOME60", text: "Home Visit 60 MINUTES" },
        { id: "HOSPSNF", text: "Hospital SNF Follow Up" },
        { id: "INJECT", text: "Injection" },
        { id: "LAB", text: "Lab" },
        { id: "MEMO", text: "Memo Appointment" },
        { id: "NAILTRIM", text: "Nailtrim 45 minutes" },
        { id: "NP60", text: "New Patient 60" },
        { id: "NURSE", text: "Nurse Visit 30 Minutes" },
        { id: "NH30", text: "Nursing Home 30 Minutes" },
        { id: "OV30", text: "Office Visit 30" },
        { id: "OV60", text: "Office Visit 60 minutes" },
        { id: "PE30", text: "Physical Exam 30 Minutes" },
        { id: "PE60", text: "Physical Exam 60" },
        { id: "PROTIME", text: "Protime" },
        { id: "SDA30", text: "Same Day 30" },
        { id: "PHONE", text: "TELEPHONE VISIT" },
        { id: "U/S60", text: "Ultrasound" },
        { id: "U/S30", text: "Ultrasound 30 minutes" },
        { id: "XR15", text: "XRAY 15 MINUTES" }
      ],

      doctors: [
        { id: 1, text: "Lab and Radiology - Creve Coeur", color: "#607d8b" },
        { id: 2, text: "Lab and Radiology - Kennerly", color: "#607d8b" },
        { id: 3, text: "Lab and Radiology - Festus", color: "#607d8b" },
        { id: 4, text: "Lab and Radiology - Alton", color: "#607d8b" },
        { id: 5, text: "Chuck Willey MD", color: "#1ca8dd" },
        { id: 6, text: "Ehab Kaiser MD", color: "#e74c3c" },
        { id: 7, text: "Raj Singh MD", color: "#56ca85" },
        { id: 8, text: "zzzzzzzEmil Fernando MD", color: "#9b59b6" },
        { id: 9, text: "Becky Gatz APRN-BC", color: "#f39c12" },
        { id: 10, text: "Erin Holloway APRN-BC", color: "#e91e63" },
        { id: 11, text: "Wayne Scrimsher APRN-BC", color: "#00bcd4" },
        { id: 12, text: "NH and Home Schedule RN", color: "#8bc34a" }
      ]
    };
  },

  watch: {
    schedulerKey() {
      this.$nextTick(() => this.applyWeekendColumnWidths());
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.applyWeekendColumnWidths();
      this.setupColumnObserver();
    });
  },

  beforeDestroy() {
    if (this._colObserver) this._colObserver.disconnect();
  },

  computed: {
    // Compute the booking end time based on selected duration
    bookingEndTime() {
      if (!this.bookingSlot.startDate) return null;
      return new Date(new Date(this.bookingSlot.startDate).getTime() + this.bookingDuration * 60000);
    },

    visibleWeekRange() {
      const d = new Date(this.currentDate);
      if (this.currentView === "Week") {
        const day = d.getDay();
        const diffToMon = day === 0 ? -6 : 1 - day;
        const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diffToMon);
        const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7);
        return { start: monday, end: sunday };
      }
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      return { start, end };
    },

    // Filter availability by office/doctor
    filteredSlots() {
      let result = this.availabilitySlots;
      if (this.selectedOffice) {
        result = result.filter((item) => item.locationId === this.selectedOffice);
      }
      if (this.selectedDoctor) {
        result = result.filter((item) => item.doctorId === this.selectedDoctor);
      }
      return result;
    },

    // Mark slots as open or booked, merge booked ranges into single items
    displayItems() {
      // Build a set of booked slot times per doctor for quick lookup
      const bookedTimes = {};
      this.appointments.forEach((appt) => {
        const docId = appt.doctorId;
        if (!bookedTimes[docId]) bookedTimes[docId] = new Set();
        const start = new Date(appt.startDate).getTime();
        const end = new Date(appt.endDate).getTime();
        const slotMs = 15 * 60000;
        for (let t = start; t < end; t += slotMs) {
          bookedTimes[docId].add(t);
        }
      });

      const items = [];

      // Add open and lunch slots (skip slots covered by a booking)
      this.filteredSlots.forEach((slot) => {
        if (slot.disabled) {
          items.push({ ...slot, slotType: "lunch" });
          return;
        }
        const docBooked = bookedTimes[slot.doctorId];
        if (docBooked && docBooked.has(slot.startDate.getTime())) {
          return; // skip — covered by a booking
        }
        items.push({ ...slot, slotType: "open" });
      });

      // Add appointments as single spanning entries
      this.appointments.forEach((appt) => {
        // Only include if it passes the current office/doctor filter
        if (this.selectedOffice && appt.locationId !== this.selectedOffice) return;
        if (this.selectedDoctor && appt.doctorId !== this.selectedDoctor) return;
        items.push({
          text: appt.patientName,
          doctorId: appt.doctorId,
          locationId: appt.locationId,
          startDate: appt.startDate,
          endDate: appt.endDate,
          slotType: "booked",
          visitReason: appt.visitReason
        });
      });

      return items.sort((a, b) => {
        const sa = new Date(a.startDate).getTime();
        const sb = new Date(b.startDate).getTime();
        if (sa !== sb) return sa - sb;
        return (a.doctorId || 0) - (b.doctorId || 0);
      });
    },

    activeDoctorIds() {
      const { start, end } = this.visibleWeekRange;
      const weekEntries = this.filteredSlots.filter(
        (item) => item.startDate >= start && item.startDate < end
      );
      return new Set(weekEntries.map((item) => item.doctorId));
    },

    filteredResources() {
      let activeDoctors;
      if (this.selectedDoctor) {
        activeDoctors = this.doctors.filter((d) => d.id === this.selectedDoctor);
      } else if (this.selectedOffice) {
        activeDoctors = this.doctors.filter((d) => this.activeDoctorIds.has(d.id));
      } else {
        activeDoctors = this.doctors;
      }
      return [
        {
          fieldExpr: "doctorId",
          dataSource: activeDoctors,
          label: "Doctor"
        }
      ];
    },

    availableDoctors() {
      if (!this.selectedOffice) return this.doctors;
      return this.doctors.filter((d) => this.activeDoctorIds.has(d.id));
    },

    legendItems() {
      const items = [];
      let labAdded = false;
      this.doctors.forEach((doc) => {
        if ([1, 2, 3, 4].includes(doc.id)) {
          if (!labAdded) {
            items.push({ id: 'lab', text: 'Lab and Radiology', color: doc.color });
            labAdded = true;
          }
        } else {
          items.push(doc);
        }
      });
      return items;
    },

    schedulerKey() {
      return 'scheduler-' + this.selectedOffice + '-' + this.selectedDoctor + '-' + this.visibleWeekRange.start.getTime() + '-' + this.bookingVersion;
    }
  },

  methods: {
    onOfficeChanged(e) {
      this.selectedOffice = e.value;
      this.selectedDoctor = null;
    },
    onDoctorChanged(e) {
      this.selectedDoctor = e.value;
    },
    onCurrentDateChanged(e) {
      this.currentDate = e.value;
    },
    onCurrentViewChanged(e) {
      this.currentView = e.value;
    },

    getSlotClass(data) {
      if (data.slotType === "lunch") return "slot-lunch";
      if (data.slotType === "booked") return "slot-booked";
      return "slot-open";
    },

    getDoctorColor(doctorId) {
      const doc = this.doctors.find((d) => d.id === doctorId);
      return doc ? doc.color : "#999";
    },

    getDoctorName(doctorId) {
      const doc = this.doctors.find((d) => d.id === doctorId);
      return doc ? doc.text : "";
    },

    getSlotStyle(data) {
      const color = this.getDoctorColor(data.doctorId);
      if (data.slotType === "lunch") {
        return { backgroundColor: "#fff3e0", borderLeft: "3px solid #ff9800", color: "#333" };
      }
      if (data.slotType === "booked") {
        return { backgroundColor: "#d0d0d0", borderLeft: "3px solid #888", color: "#333" };
      }
      // Open slot: solid doctor color background matching legend
      return { backgroundColor: color, borderLeft: "3px solid " + color, color: "#fff" };
    },

    onSlotClick(e) {
      e.cancel = true;
      const data = e.appointmentData;

      // Don't allow booking lunch blocks
      if (data.slotType === "lunch") return;

      // Edit mode for booked slots
      if (data.slotType === "booked") {
        const clickedStart = new Date(data.startDate).getTime();
        const index = this.appointments.findIndex(
          (a) => a.doctorId === data.doctorId && new Date(a.startDate).getTime() === clickedStart
        );
        if (index === -1) return;
        const appt = this.appointments[index];
        const apptStart = new Date(appt.startDate);
        const apptEnd = new Date(appt.endDate);
        const apptDuration = (apptEnd - apptStart) / 60000;

        // Compute available durations (treat this booking's slots as free)
        const slotMs = 15 * 60000;
        const dayStart = new Date(apptStart);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);
        const openTimes = new Set();
        this.availabilitySlots.forEach((s) => {
          if (s.doctorId === appt.doctorId && !s.disabled && s.startDate >= dayStart && s.startDate < dayEnd) {
            openTimes.add(s.startDate.getTime());
          }
        });
        this.appointments.forEach((a, i) => {
          if (i === index) return;
          if (a.doctorId === appt.doctorId) {
            const aStart = new Date(a.startDate).getTime();
            const aEnd = new Date(a.endDate).getTime();
            for (let bt = aStart; bt < aEnd; bt += slotMs) openTimes.delete(bt);
          }
        });
        let count = 0;
        let t = apptStart.getTime();
        while (openTimes.has(t)) { count++; t += slotMs; }
        const durations = [];
        for (let i = 1; i <= count; i++) {
          const mins = i * 15;
          const label = mins >= 60 ? (mins / 60) + " hr" + (mins > 60 ? "s" : "") : mins + " min";
          durations.push({ value: mins, text: label });
        }

        this.durationOptions = durations;
        this.bookingDuration = apptDuration;
        this.editingIndex = index;
        this.isEditing = true;
        this.bookingSlot = {
          startDate: apptStart,
          endDate: apptEnd,
          doctorId: appt.doctorId,
          locationId: appt.locationId,
          patientName: appt.patientName,
          appointmentType: appt.appointmentType || null,
          visitReason: appt.visitReason || ""
        };
        this.showBookingDialog = true;
        return;
      }

      // Ensure dates are Date objects
      const startDate = new Date(data.startDate);
      const endDate = new Date(data.endDate);
      const doctorId = data.doctorId;
      const locationId = data.locationId;

      // Calculate available consecutive durations
      const slotMs = 15 * 60000;
      const startTime = startDate.getTime();

      const dayStart = new Date(startDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      // Collect open slot times for this doctor on this day
      const openTimes = new Set();
      this.availabilitySlots.forEach((s) => {
        if (
          s.doctorId === doctorId &&
          !s.disabled &&
          s.startDate >= dayStart &&
          s.startDate < dayEnd
        ) {
          openTimes.add(s.startDate.getTime());
        }
      });

      // Remove already-booked times
      this.appointments.forEach((appt) => {
        if (appt.doctorId === doctorId) {
          const aStart = new Date(appt.startDate).getTime();
          const aEnd = new Date(appt.endDate).getTime();
          for (let bt = aStart; bt < aEnd; bt += slotMs) {
            openTimes.delete(bt);
          }
        }
      });

      // Count consecutive open slots
      let count = 0;
      let t = startTime;
      while (openTimes.has(t)) {
        count++;
        t += slotMs;
      }

      const durations = [];
      for (let i = 1; i <= count; i++) {
        const mins = i * 15;
        const label = mins >= 60
          ? (mins / 60) + " hr" + (mins > 60 ? "s" : "")
          : mins + " min";
        durations.push({ value: mins, text: label });
      }

      this.durationOptions = durations;
      this.bookingDuration = 15;
      this.bookingSlot = {
        startDate,
        endDate,
        doctorId,
        locationId,
        patientName: "",
        appointmentType: [1, 2, 3, 4].includes(doctorId) ? "LAB" : null,
        visitReason: ""
      };
      this.showBookingDialog = true;
    },

    confirmBooking() {
      const startTime = new Date(this.bookingSlot.startDate).getTime();
      const endTime = startTime + this.bookingDuration * 60000;

      this.appointments.push({
        doctorId: this.bookingSlot.doctorId,
        locationId: this.bookingSlot.locationId,
        startDate: new Date(startTime),
        endDate: new Date(endTime),
        patientName: this.bookingSlot.patientName,
        appointmentType: this.bookingSlot.appointmentType,
        visitReason: this.bookingSlot.visitReason
      });
      this.closeBookingDialog();
      this.bookingVersion++;
    },

    saveEdit() {
      const startTime = new Date(this.bookingSlot.startDate).getTime();
      const endTime = startTime + this.bookingDuration * 60000;
      this.appointments.splice(this.editingIndex, 1, {
        doctorId: this.bookingSlot.doctorId,
        locationId: this.bookingSlot.locationId,
        startDate: new Date(startTime),
        endDate: new Date(endTime),
        patientName: this.bookingSlot.patientName,
        appointmentType: this.bookingSlot.appointmentType,
        visitReason: this.bookingSlot.visitReason
      });
      this.closeBookingDialog();
      this.bookingVersion++;
    },

    cancelAppointment() {
      this.appointments.splice(this.editingIndex, 1);
      this.closeBookingDialog();
      this.bookingVersion++;
    },

    closeBookingDialog() {
      this.showBookingDialog = false;
      this.isEditing = false;
      this.editingIndex = -1;
      this.bookingSlot = {
        startDate: null,
        endDate: null,
        doctorId: null,
        locationId: null,
        patientName: "",
        appointmentType: null,
        visitReason: ""
      };
      this.bookingDuration = 15;
    },

    formatDateTime(date) {
      if (!date) return "";
      return date.toLocaleDateString() + " " + this.formatTime(date);
    },

    formatTime(date) {
      if (!date) return "";
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },

    applyWeekendColumnWidths() {
      const workspace = this.$el && this.$el.querySelector('.dx-scheduler-work-space');
      if (!workspace) return;
      workspace.querySelectorAll('table').forEach((table) => {
        const cols = Array.from(table.querySelectorAll(':scope > colgroup > col'));
        if (cols.length !== 7) return;
        const total = cols.reduce((sum, c) => sum + (parseFloat(c.style.width) || 0), 0);
        if (total === 0) return;
        const weekendW = (total / 7) * 0.2;
        const workdayW = (total - weekendW * 2) / 5;
        this._applyingCols = true;
        cols[0].style.width = weekendW + 'px';
        cols[6].style.width = weekendW + 'px';
        for (let i = 1; i <= 5; i++) cols[i].style.width = workdayW + 'px';
        this._applyingCols = false;
      });
    },

    setupColumnObserver() {
      const workspace = this.$el && this.$el.querySelector('.dx-scheduler-work-space');
      if (!workspace) return;
      this._colObserver = new MutationObserver((mutations) => {
        if (this._applyingCols) return;
        const hasColStyle = mutations.some(
          (m) => m.target.tagName === 'COL' && m.type === 'attributes'
        );
        if (hasColStyle) this.applyWeekendColumnWidths();
      });
      this._colObserver.observe(workspace, {
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
    }
  }
};
</script>

<style>
#app {
  height: 100%;
}

/* Slot type styles */
.slot-open {
  background-color: #e8f5e9;
  border-left: 3px solid #4caf50;
}
.slot-booked {
  background-color: #e3f2fd;
  border-left: 3px solid #1976d2;
}
.slot-lunch {
  background-color: #fff3e0;
  border-left: 3px solid #ff9800;
}

/* Booking dialog */
.booking-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.booking-dialog {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  min-width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}
.booking-dialog h3 {
  margin-top: 0;
  margin-bottom: 16px;
}
.booking-field {
  margin-bottom: 12px;
}
.booking-field label {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 13px;
}
.booking-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}
.booking-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  justify-content: flex-end;
}
.btn-book {
  background: #1976d2;
  color: #fff;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.btn-book:disabled {
  background: #aaa;
  cursor: not-allowed;
}
.btn-cancel {
  background: #e0e0e0;
  color: #333;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.btn-danger {
  background: #e53935;
  color: #fff;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

/* Keep weekend header/cell text compact once JS resizes the cols */
#app .dx-scheduler-header-panel th.dx-scheduler-header-panel-cell:has(.dx-scheduler-header-panel-week-cell[class*='sat']),
#app .dx-scheduler-header-panel th.dx-scheduler-header-panel-cell:has(.dx-scheduler-header-panel-week-cell[class*='sun']) {
  overflow: hidden;
  white-space: nowrap;
}
</style>